import { Divider } from '@mui/material';
import { Typography } from '@uhc-tempo/components';
import {
    SHELL_INN_PROVIDER,
    CARE_PATH_CATEGORY_CODE,
    ENTER_KEY,
    TAB_KEY,
    SHIFT_KEY,
    ARROW_DOWN_KEY,
    CURSOR_POINTER,
} from '../../../constants';
import React from 'react';

const ProviderNoOptionsDropdown = (props) => {
    const {
        isIndemnity,
        innNetworkGacFlow,
        setProvider,
        handleSubmit,
        service,
        setProviderSearchOpen,
        setNavigateWithKey,
        navigateWithKey,
        umrIndemnity,
    } = props;

    const GAC_ONLY_PARAMS = {
        gacOnly: true,
    };

    const OON_PARAMS = {
        oon: true,
    };

    const selectedServiceCoverageType = service?.value?.fullCarePath?.coverageTypeCode;

    /**
     * Currently we don't provide an INN estimate for dental services if the user does not have networks/PNI
     * (i.e. GAC flow does not exist for dental).
     * If there are no options for providers & the selected service is dental,
     * we will not display the link to get an inn estimate in the provider search dropdown
     */
    const displayInnEstimateLink = () => {
        return !(selectedServiceCoverageType == CARE_PATH_CATEGORY_CODE.DENTAL);
    };

    /**
     * for indemnity members, they don't have a network of providers so we don't want to provide
     * links for either INN or OON estimates. Instead we just give them the generic link to click for avg
     * cost, which will trigger a GAC only request. In rate engine, indemnity members are detected and OON
     * rates are returned
     */
    const renderIndemnityDropdown = () => {
        return (
            <div
                aria-live="polite" //announce no providers found
                aria-labelledby="noProvidersText linkToAverageCost"
                aria-atomic="true" //needed due to using labelledby
            >
                <div>
                    <Typography.Paragraph id="noProvidersText">
                        We're unable to determine your network of providers.
                    </Typography.Paragraph>
                    <Typography.Link
                        aria-label="Provider Search Average Cost link" //does not mention it is a link using default behavior
                        id="providerSearchLinkToAverageCost"
                        style={{ cursor: CURSOR_POINTER }}
                        onBlur={() => {
                            if (!navigateWithKey) {
                                setProviderSearchOpen(false);
                            }
                            setNavigateWithKey(false);
                        }}
                        onClick={() => {
                            setProvider(SHELL_INN_PROVIDER);
                            handleSubmit(GAC_ONLY_PARAMS);
                        }}
                        onKeyDown={(e) => {
                            if (e.key == ENTER_KEY) {
                                setProvider(SHELL_INN_PROVIDER);
                                handleSubmit(GAC_ONLY_PARAMS);
                            }
                            if (e.key == TAB_KEY) {
                                setNavigateWithKey(true);
                            }
                            if (e.key == SHIFT_KEY) {
                                setNavigateWithKey(true);
                            }
                        }}
                        tabIndex="0"
                    >
                        Click here for the average cost for this service in your area.
                    </Typography.Link>
                </div>
            </div>
        );
    };

    const renderStandardNoOptionsDropdown = () => {
        return (
            <div
                aria-live="polite" //announce no providers found
                aria-labelledby="noProvidersText INNLink OONLink"
                aria-atomic="true" //needed due to using labelledby
            >
                <div>
                    {innNetworkGacFlow ? (
                        <Typography.Paragraph id="noProvidersText">
                            We are unable to locate providers in your geographic area that perform
                            this service/item. Click one of the options below for the average cost
                            for this service in your area.
                        </Typography.Paragraph>
                    ) : (
                        <Typography.Paragraph id="noProvidersText">
                            We are unable to find this provider. Please check the spelling and try
                            again or expand your search.
                        </Typography.Paragraph>
                    )}
                    <Divider />
                </div>
                <div>
                    {displayInnEstimateLink() ? (
                        <Typography.Link
                            aria-label="In-network Estimate link" //does not mention it is a link using default behavior
                            id="INNLink"
                            style={{ cursor: CURSOR_POINTER }}
                            onBlur={() => {
                                if (!navigateWithKey) {
                                    setProviderSearchOpen(false);
                                }
                                setNavigateWithKey(false);
                            }}
                            onClick={() => {
                                if (innNetworkGacFlow) {
                                    setProvider(SHELL_INN_PROVIDER);
                                    handleSubmit(GAC_ONLY_PARAMS);
                                } else {
                                    handleSubmit({
                                        allProviders: true,
                                    });
                                }
                            }}
                            onKeyDown={(e) => {
                                if (e.key == ENTER_KEY) {
                                    if (innNetworkGacFlow) {
                                        setProvider(SHELL_INN_PROVIDER);
                                        handleSubmit(GAC_ONLY_PARAMS);
                                    } else {
                                        handleSubmit({
                                            allProviders: true,
                                        });
                                    }
                                }
                                if (e.key == TAB_KEY) {
                                    setNavigateWithKey(true);
                                }
                                if (e.key == SHIFT_KEY) {
                                    setNavigateWithKey(true);
                                }
                                if (e.key == ARROW_DOWN_KEY) {
                                    document.getElementById('OONLink').focus();
                                }
                            }}
                            tabIndex="0"
                        >
                            In-network Estimate
                        </Typography.Link>
                    ) : null}
                </div>
                <div>
                    <Typography.Link
                        aria-label="Out-of-network Estimate link"
                        id="OONLink"
                        style={{ cursor: CURSOR_POINTER }}
                        onBlur={() => {
                            if (!navigateWithKey) {
                                setProviderSearchOpen(false);
                            }
                            setNavigateWithKey(false);
                        }}
                        onClick={() => {
                            handleSubmit(OON_PARAMS);
                        }}
                        onKeyDown={(e) => {
                            if (e.key == ENTER_KEY) {
                                handleSubmit(OON_PARAMS);
                            }
                            if (e.key == SHIFT_KEY) {
                                setNavigateWithKey(true);
                            }
                        }}
                        tabIndex="0"
                    >
                        Out-of-network Estimate
                    </Typography.Link>
                </div>
            </div>
        );
    };

    return isIndemnity[selectedServiceCoverageType] || umrIndemnity[selectedServiceCoverageType]
        ? renderIndemnityDropdown()
        : renderStandardNoOptionsDropdown();
};
export default ProviderNoOptionsDropdown;
